import { type Color } from '../math/Color';
import { DebugShapeKind, type DebugShape } from './DebugShapes';

/**
 * The frame's debug shape list: what to draw, in world units, and nothing about how.
 *
 * The engine collects, something else paints. That split is what keeps debug draw out of the
 * `Renderer` interface, which exists to draw a game and would grow a line API no game asks for.
 * `@kite/editor` renders this list onto its own overlay, so a production bundle carries the collector
 * and never a pixel of it.
 *
 * Shapes live for one frame. Anything added during a frame is visible to the debug stage at the end of
 * it and gone before the next one starts, so a behavior draws unconditionally every frame rather than
 * tracking what it drew last time.
 *
 * While {@link DebugDraw.enabled} is false every call is a no-op, which is what lets a game leave debug
 * draw calls in shipping code.
 */
export class DebugDraw {
  /** Whether shapes are collected at all. Owned by `DebugService`, which is where hosts set it. */
  enabled = false;

  readonly #shapes: DebugShape[] = [];

  /** This frame's shapes so far, in the order they were added. */
  get shapes(): readonly DebugShape[] {
    return this.#shapes;
  }

  /** Adds a segment between two world points. */
  line(startX: number, startY: number, endX: number, endY: number, color: Color): void {
    if (!this.enabled) {
      return;
    }
    this.#shapes.push({ kind: DebugShapeKind.Line, startX, startY, endX, endY, color });
  }

  /** Adds a rectangle centered on a world point, rotated counter-clockwise by `rotation` radians. */
  rect(centerX: number, centerY: number, width: number, height: number, color: Color, rotation = 0): void {
    if (!this.enabled) {
      return;
    }
    this.#shapes.push({ kind: DebugShapeKind.Rect, centerX, centerY, width, height, rotation, color });
  }

  /** Adds a circle centered on a world point. */
  circle(centerX: number, centerY: number, radius: number, color: Color): void {
    if (!this.enabled) {
      return;
    }
    this.#shapes.push({ kind: DebugShapeKind.Circle, centerX, centerY, radius, color });
  }

  /** Empties the list. Called once per frame by `DebugSystem`, after the frame has been published. */
  clear(): void {
    this.#shapes.length = 0;
  }
}
